import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetNationalHolisticAnalyticsQuery,
  mergeNationalAnalyticsResponses,
  TermFilter,
  termFilterToQueryValue,
  getClassDisplayName,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';

const scaleToPercent = (val: number): number => {
  if (val <= 1.0 && val > 0) return +(val * 100).toFixed(1);
  return +val.toFixed(1);
};

const TeacherAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    if (!records.length) {
      return {
        strengths: [],
        challenges: [],
        studentsList: [],
      };
    }

    let problemSolving = 0;
    let conceptClarity = 0;
    let logicalThinking = 0;
    let activeParticipation = 0;
    let concentrationFocus = 0;
    let curiosityInquiry = 0;

    let conceptReinforce = 0;
    let slowPace = 0;
    let doubtClarification = 0;
    let attentionSupport = 0;
    let taskCompletion = 0;
    let classParticipation = 0;

    const list = records.map((record) => {
      const cogPercent = scaleToPercent(Number(record.cognitive_score || 0));
      const habitPercent = scaleToPercent(Number(record.learning_habits_score || 0));

      if (cogPercent >= 80) {
        problemSolving++;
        conceptClarity++;
        logicalThinking++;
      } else if (cogPercent < 55) {
        conceptReinforce++;
        slowPace++;
        doubtClarification++;
      }

      if (habitPercent >= 80) {
        activeParticipation++;
        concentrationFocus++;
        curiosityInquiry++;
      } else if (habitPercent < 55) {
        attentionSupport++;
        taskCompletion++;
        classParticipation++;
      }

      const first = (record.firstName ?? record.first_name ?? '').trim();
      const middle = (record.middleName ?? record.middle_name ?? '').trim();
      const last = (record.lastName ?? record.last_name ?? '').trim();
      const fullName = [first, middle, last].filter(Boolean).join(' ') || `Student ${record.student_id}`;

      return {
        id: record.student_id,
        name: fullName,
        className: getClassDisplayName(Number(record.class_id)),
        section: '-',
        observationDetail: 'Detailed teacher comments & specific suggestions are available on the profile page.',
        regionId: record.region_id,
        schoolId: record.school_id,
        classId: record.class_id,
      };
    });

    const strengths = [
      { name: 'Problem Solving', count: problemSolving },
      { name: 'Conceptual Clarity', count: conceptClarity },
      { name: 'Logical Thinking', count: logicalThinking },
      { name: 'Active Participation', count: activeParticipation },
      { name: 'Concentration & Focus', count: concentrationFocus },
      { name: 'Curiosity & Inquiry', count: curiosityInquiry },
    ].sort((a, b) => b.count - a.count);

    const challenges = [
      { name: 'Needs Concept Reinforcement', count: conceptReinforce },
      { name: 'Slower Learning Pace', count: slowPace },
      { name: 'Doubt Clarification Needed', count: doubtClarification },
      { name: 'Needs Attention Support', count: attentionSupport },
      { name: 'Task Completion Issues', count: taskCompletion },
      { name: 'Low Class Participation', count: classParticipation },
    ].sort((a, b) => b.count - a.count);

    return {
      strengths,
      challenges,
      studentsList: list.slice(0, 50), // Show first 50
    };
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Teacher Observations</h1>
          <p className="text-sm text-gray-500">Aggregated insights from teacher assessments across {records.length} students</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Strengths */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-emerald-700 mb-4">Most Common Strengths</h3>
          <div className="space-y-3">
            {stats.strengths.map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">{item.name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 rounded-full"
                      style={{ width: `${records.length > 0 ? (item.count / records.length) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-6 text-right">{item.count}</span>
                </div>
              </div>
            ))}
            {stats.strengths.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-6">No data available</div>
            )}
          </div>
        </div>

        {/* Top Challenges */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-amber-700 mb-4">Most Common Challenges</h3>
          <div className="space-y-3">
            {stats.challenges.map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="inline-block px-3 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-full">{item.name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-500 rounded-full"
                      style={{ width: `${records.length > 0 ? (item.count / records.length) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-6 text-right">{item.count}</span>
                </div>
              </div>
            ))}
            {stats.challenges.length === 0 && (
              <div className="text-center text-sm text-gray-400 py-6">No data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Student Recommendations */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Teacher Recommendations & Observations by Student</h3>
          <span className="text-xs text-gray-400">Showing first 50 students</span>
        </div>
        <div className="divide-y divide-gray-100">
          {stats.studentsList.map((student: any) => (
            <div
              key={student.id}
              onClick={() => navigate(`/kvs-samagam/student-analytics/region/${student.regionId}/school/${student.schoolId}/class/${student.classId}/students/${student.id}/teacher?year=${academicYear}&term=${termFilterToQueryValue(term)}`)}
              className="px-6 py-4 hover:bg-indigo-50/40 cursor-pointer transition flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                  {student.name.split(' ').map((n: string) => n[0]).join('')}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{student.name}</p>
                  <p className="text-xs text-gray-400">{student.className}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <p className="text-xs text-gray-400 hidden sm:block max-w-xs truncate">{student.observationDetail}</p>
                <span className="text-xs text-indigo-600 font-semibold hover:underline">
                  View Observations & Suggestions →
                </span>
              </div>
            </div>
          ))}
          {stats.studentsList.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-400">No data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherAnalysis;
